import { useEffect, useState, useCallback } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Container,
  Stack,
  Snackbar,
  Alert,
  Divider,
  CircularProgress
} from "@mui/material";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LogoutIcon from "@mui/icons-material/Logout";
import LockResetIcon from "@mui/icons-material/LockReset";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import { fetchAuthSession, signOut } from "aws-amplify/auth";
import { get } from "aws-amplify/api";
import { AuthContext } from "./libs/AuthContext";
import { useColorMode } from "./libs/ColorMode";
import AppRoutes from "./Routes";

const navLinkSx = (active) => ({
  color: "text.primary",
  fontWeight: active ? 700 : 500,
  fontSize: "0.9rem",
  textTransform: "none",
  px: 1.5,
  borderRadius: 1.5
});

function NavLink({ to, label, currentPath }) {
  const active = currentPath === to;
  return (
    <Button component={RouterLink} to={to} sx={navLinkSx(active)} variant="text">
      {label}
    </Button>
  );
}

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, toggle } = useColorMode();
  const [auth, setAuth] = useState({
    emailAddress: "",
    isAuthenticated: false,
    isAuthenticating: true,
    isAdminUser: false,
    isLicenseManager: false,
    isLicenseManagerAdmin: false
  });
  const [globalError, setGlobalError] = useState("");
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  const refreshAuth = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      if (!session?.tokens?.accessToken) {
        setAuth({ emailAddress: "", isAuthenticated: false, isAuthenticating: false, isAdminUser: false, isLicenseManager: false, isLicenseManagerAdmin: false });
        return;
      }
      // licenceId may be missing for license-manager-only users (no
      // DataStarUsers row, no tenant licence). The /license call fails
      // for them — that's fine, we just can't compute isAdminUser.
      let licenseId = sessionStorage.getItem("licenseId");
      if (licenseId == null) {
        try {
          const op = get({ apiName: "license", path: "/license" });
          const { body } = await op.response;
          const license = await body.json();
          if (license?.licenseId) {
            sessionStorage.setItem("licenseId", license.licenseId);
            licenseId = license.licenseId;
          }
        } catch (_) {
          // No licence assigned to this user — treat as license-manager-only.
        }
      }
      const groups = session.tokens.accessToken.payload["cognito:groups"] ?? [];
      const isAdminUser = Boolean(licenseId) && groups.includes(`${licenseId}.admin`);
      const isLicenseManagerAdmin = groups.includes("absolute.license-manager-admin");
      // Treat admins as managers too — server-side promotion always
      // grants both groups, but be tolerant of someone who was put
      // straight into the admin group via the Cognito console without
      // the manager group.
      const isLicenseManager = isLicenseManagerAdmin || groups.includes("absolute.license-manager");
      const userEmail = session.tokens.idToken?.payload?.email ?? "";
      setAuth({
        emailAddress: userEmail,
        isAuthenticated: true,
        isAuthenticating: false,
        isAdminUser,
        isLicenseManager,
        isLicenseManagerAdmin
      });
    } catch (e) {
      setAuth({ emailAddress: "", isAuthenticated: false, isAuthenticating: false, isAdminUser: false, isLicenseManager: false, isLicenseManagerAdmin: false });
      if (e?.message && e.message !== "No current user") {
        setGlobalError(e.message);
      }
    }
  }, []);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  const userHasAuthenticated = useCallback(() => {
    refreshAuth();
  }, [refreshAuth]);

  const emailAddressHandler = useCallback((email) => {
    setAuth((prev) => ({ ...prev, emailAddress: email }));
  }, []);

  const handleLogout = async () => {
    setUserMenuAnchor(null);
    await signOut();
    sessionStorage.removeItem("licenseId");
    setAuth({ emailAddress: "", isAuthenticated: false, isAuthenticating: false, isAdminUser: false, isLicenseManager: false, isLicenseManagerAdmin: false });
    navigate("/login");
  };

  if (auth.isAuthenticating) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  const contextValue = {
    emailAddress: auth.emailAddress,
    isAuthenticated: auth.isAuthenticated,
    isAdminUser: auth.isAdminUser,
    isLicenseManager: auth.isLicenseManager,
    isLicenseManagerAdmin: auth.isLicenseManagerAdmin,
    userHasAuthenticated,
    emailAddressHandler
  };

  const userInitial = auth.emailAddress ? auth.emailAddress.charAt(0).toUpperCase() : "?";

  return (
    <AuthContext.Provider value={contextValue}>
      <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "background.default" }}>
        <AppBar position="sticky">
          <Toolbar sx={{ gap: 1, minHeight: { xs: 56, sm: 64 } }}>
            <Box
              component={RouterLink}
              to={auth.isAuthenticated ? "/" : "/login"}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                textDecoration: "none",
                color: "text.primary",
                mr: 2
              }}
            >
              <Box
                component="img"
                src="/images/logo.png"
                alt=""
                sx={{ height: 32, width: 32, borderRadius: 1 }}
              />
              <Box
                sx={{
                  fontSize: "1rem",
                  fontWeight: 600,
                  letterSpacing: "-0.01em",
                  display: { xs: "none", sm: "inline" }
                }}
              >
                DataStar Licensing
              </Box>
            </Box>

            {auth.isAuthenticated && (
              <Stack direction="row" spacing={0.5} sx={{ display: { xs: "none", sm: "flex" } }}>
                <NavLink to="/" label="Home" currentPath={location.pathname} />
                {auth.isAdminUser && (
                  <>
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1.5 }} />
                    <NavLink to="/admin/users" label="Users" currentPath={location.pathname} />
                    <NavLink to="/admin/register" label="Register User" currentPath={location.pathname} />
                  </>
                )}
                {auth.isLicenseManager && (
                  <>
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1.5 }} />
                    <NavLink to="/admin/licenses" label="Licences" currentPath={location.pathname} />
                    <NavLink to="/admin/organizations/new" label="New Organisation" currentPath={location.pathname} />
                    <NavLink to="/admin/managers" label="Managers" currentPath={location.pathname} />
                  </>
                )}
              </Stack>
            )}

            <Box sx={{ flexGrow: 1 }} />

            <Button
              component="a"
              href="https://datastar.software"
              target="_blank"
              rel="noopener"
              variant="text"
              size="small"
              endIcon={<OpenInNewIcon sx={{ fontSize: "0.95rem !important" }} />}
              sx={{
                color: "text.secondary",
                fontWeight: 500,
                fontSize: "0.875rem",
                px: 1.5,
                display: { xs: "none", sm: "inline-flex" },
                "&:hover": { color: "text.primary", bgcolor: "transparent" }
              }}
            >
              Docs
            </Button>

            <Tooltip title={mode === "dark" ? "Switch to light mode" : "Switch to dark mode"}>
              <IconButton onClick={toggle} color="inherit" size="small">
                {mode === "dark" ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
              </IconButton>
            </Tooltip>

            {auth.isAuthenticated ? (
              <>
                <Tooltip title={auth.emailAddress || "Account"}>
                  <IconButton
                    onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                    size="small"
                    sx={{ ml: 0.5 }}
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        fontSize: "0.85rem",
                        fontWeight: 600
                      }}
                    >
                      {userInitial}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={() => setUserMenuAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  slotProps={{
                    paper: { sx: { mt: 1, minWidth: 220, borderRadius: 2 } }
                  }}
                >
                  {auth.emailAddress && (
                    <Box sx={{ px: 2, py: 1, fontSize: "0.8rem", color: "text.secondary" }}>
                      Signed in as
                      <Box sx={{ color: "text.primary", fontWeight: 500, fontSize: "0.875rem" }}>
                        {auth.emailAddress}
                      </Box>
                    </Box>
                  )}
                  <Divider />
                  <MenuItem
                    component={RouterLink}
                    to="/settings/password"
                    onClick={() => setUserMenuAnchor(null)}
                  >
                    <LockResetIcon fontSize="small" sx={{ mr: 1.5 }} />
                    Change Password
                  </MenuItem>
                  {auth.isLicenseManager && (
                    <MenuItem
                      component={RouterLink}
                      to="/admin/setup-mfa"
                      onClick={() => setUserMenuAnchor(null)}
                    >
                      <LockResetIcon fontSize="small" sx={{ mr: 1.5 }} />
                      Set up MFA
                    </MenuItem>
                  )}
                  <MenuItem
                    component={RouterLink}
                    to="/about"
                    onClick={() => setUserMenuAnchor(null)}
                  >
                    <InfoOutlinedIcon fontSize="small" sx={{ mr: 1.5 }} />
                    About
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} />
                    Sign Out
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Stack direction="row" spacing={1}>
                <Button
                  component={RouterLink}
                  to="/login"
                  variant="text"
                  sx={navLinkSx(location.pathname === "/login")}
                >
                  Sign In
                </Button>
                <Button
                  component={RouterLink}
                  to="/register"
                  variant="contained"
                  size="small"
                  sx={{ fontWeight: 600 }}
                >
                  Register
                </Button>
              </Stack>
            )}
          </Toolbar>
        </AppBar>

        <Box component="main" sx={{ flexGrow: 1, py: { xs: 3, sm: 5 } }}>
          <Container maxWidth="lg">
            <AppRoutes />
          </Container>
        </Box>

        <Box
          component="footer"
          sx={{
            py: 3,
            borderTop: 1,
            borderColor: "divider",
            color: "text.secondary",
            fontSize: "0.85rem",
            textAlign: "center"
          }}
        >
          © {new Date().getFullYear()} Absolute Technology ·{" "}
          <Box
            component="a"
            href="https://datastar.software"
            target="_blank"
            rel="noopener"
            sx={{
              color: "inherit",
              textDecoration: "none",
              "&:hover": { color: "text.primary", textDecoration: "underline" }
            }}
          >
            datastar.software ↗
          </Box>
        </Box>

        <Snackbar
          open={Boolean(globalError)}
          autoHideDuration={6000}
          onClose={() => setGlobalError("")}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity="error" variant="filled" onClose={() => setGlobalError("")}>
            {globalError}
          </Alert>
        </Snackbar>
      </Box>
    </AuthContext.Provider>
  );
}
